/*
 * Module for handling the headers of a sortable list
 */

define([
  "marionette",
  "backbone.syphon",
  "jquery",
  "underscore",
  "messaging/messenger",
  "modules/shared/sortableList/sortableListHead/sortableListHeadView",
], function (Marionette, BackboneSyphon, $, _, Messenger, SortableListHeadView) {
  "use strict";

  return Marionette.Controller.extend({
    initialize: function (options) {
      this.name = "sortableListHead";
      this.region = options.region;
      this.headers = options.headers;
      this.collection = null;
      this.sortedHeaderView = null; //view of the actively sorted header
      this.filterPopover = null; //open popover
      this.namespace = options.namespace;
      if (this.namespace) {
        this.namespacePrefix = this.namespace + ":";
      } else {
        this.namespacePrefix = "";
      }
      this._setUpVents();
    },
    show: function (listCollection) {
      this.collection = listCollection;
      this.view = new SortableListHeadView.sortableListHeadView({ collection: this.headers });

      if (this.headers) {
        //if the headers collection gets reset (columns added or removed), re-render the view and call for the body
        // to be re-drawn
        this.listenTo(this.headers, "reset", function () {
          console.log("header re-render");
          this.view.render();
          //redraw the body by refiltering the list
          Messenger.vent.trigger(this.namespacePrefix + "filter:list", this._filterFunction());
        });
      }

      this.view.on(
        "itemview:sort:header",
        function (view) {
          this._sortByHeader(view);
        },
        this,
      );

      this.view.on(
        "itemview:click:filter",
        function (view) {
          console.log("clicked on filter");
          if (this.filterPopover === null) {
            this.filterPopover = view.insertFilterMenu(this.collection);
          }
        },
        this,
      );

      this.view.on(
        "itemview:cancel:filter",
        function (view) {
          this._closeFilterMenu(view);
        },
        this,
      );

      this.view.on(
        "itemview:clear:filter",
        function (view) {
          view.model.filterFunction = null;
          view.model.filter = null;
          this._closeFilterMenu(view);
          this.view.render();
          //fire event to refilter list
          Messenger.vent.trigger(this.namespacePrefix + "filter:list", this._filterFunction());
        },
        this,
      );

      this.view.on(
        "itemview:set:filter",
        function (view) {
          this._setUpFilter(view);
          this._closeFilterMenu(view);
          this.view.render();
          //fire event to filter list with the provided filter function
          Messenger.vent.trigger(this.namespacePrefix + "filter:list", this._filterFunction());
        },
        this,
      );

      this.view.on(
        "itemview:toggle:checkbox",
        function () {
          //fire event that all the models should be toggled
          Messenger.vent.trigger(
            this.namespacePrefix + "toggle:all:checkboxes",
            !this.headers.checked,
          );
        },
        this,
      );

      this.showRegion(this.view);
    },
    defaultSort: function () {
      let leftmostSortableHeader;
      let defaultSortHeader;
      //sort in increasing order the leftmost sortable column
      this.view.children.each(function (view) {
        //if we haven't found a default sort header and this header is sortable
        if (!defaultSortHeader && view.model.get("sortable")) {
          if (view.model.get("defaultSort")) {
            defaultSortHeader = view;
          }
          if (!leftmostSortableHeader) {
            leftmostSortableHeader = view;
          }
        }
      }, this);
      //sort by the default sort header, or by the leftmost sortable header, if one exists
      if (defaultSortHeader) {
        this._sortByHeader(defaultSortHeader);
      } else if (leftmostSortableHeader) {
        this._sortByHeader(leftmostSortableHeader);
      }
    },
    _closeFilterMenu: function () {
      if (this.filterPopover) {
        this.filterPopover.popover("hide");
        this.filterPopover = null;
      }
    },
    _filterFunction: function () {
      const headers = this.headers;
      const externalFilterFunction = this.externalFilterFunction;
      return function (model) {
        //build filter fuction from all the headers' filter functions
        let keepModel = true;
        //apply external filter function, if applicable
        if (externalFilterFunction) {
          keepModel = externalFilterFunction(model);
        }
        headers.each(function (header) {
          //every filterFunction must evaluate to true in order to keep the model (ie. not filter it out)
          if (keepModel && header.filterFunction) {
            keepModel = header.filterFunction(model);
          }
        });
        return keepModel;
      };
    },
    _setUpFilter: function (view) {
      const data = BackboneSyphon.serialize(view);
      //assign filter results to header
      view.model.filter = data;
      console.log(JSON.stringify(data));
      //set up this header's filterFunction() based on the parameters of the filter
      let attr = view.model.get("modelAttribute");
      //get the attribute name if the attribute is attached to the object (using _thisObject)
      let backboneAttr = true;
      if (attr.startsWith("_thisObject.")) {
        attr = attr.slice("_thisObject.".length); //get the variable name without _thisObject. in front
        backboneAttr = false;
      }
      view.model.filterFunction = function (model) {
        const value = backboneAttr ? model.get(attr) : model[attr];
        if (view.model.get("textField")) {
          //check if model's value is selected in the filter
          return view.model.filter[value];
        } else if (view.model.get("booleanField")) {
          if (value) {
            return view.model.filter[$.t("common.yes")];
          } else {
            return view.model.filter[$.t("common.no")];
          }
        } else if (view.model.get("commaSeparatedField")) {
          if (value) {
            const values = value.split(",");
            for (let c = 0; c < values.length; c++) {
              const v = values[c];
              if (view.model.filter[v]) {
                return true;
              }
            }
          }
          return false;
        } else if (view.model.get("numericField")) {
          const filter = view.model.filter;
          if (filter.Minimum === "" && filter.Maximum === "") {
            return true;
          }
          if (value === null || value === undefined) {
            return false;
          }
          return (
            (filter.Minimum === "" || value >= filter.Minimum) &&
            (filter.Maximum === "" || value <= filter.Maximum)
          );
        } else if (view.model.get("dateField")) {
          const filter = view.model.filter;
          // If no filter has been applied, then all values match
          if (!filter.Minimum && !filter.Maximum) {
            return true;
          }
          // If at least a minimum or a maximum has been set, then empty dates no longer match
          if (!value) {
            return false;
          }
          if (filter.Minimum) {
            const minimum = Messenger.reqres.request("to:date", filter.Minimum);
            if (value < minimum) {
              return false;
            }
          }
          if (filter.Maximum) {
            const maximum = Messenger.reqres.request("to:date", filter.Maximum);
            if (value > maximum) {
              return false;
            }
          }
          return true;
        } else if (view.model.get("longTextField")) {
          const filterText = view.model.filter.text;
          if (!filterText) {
            return true;
          }
          if (!value) {
            return false;
          }
          //return true if the filter text is contained in the value (ignore case)
          return value.toLowerCase().indexOf(filterText.toLowerCase()) !== -1;
        } else if (view.model.get("arrayField")) {
          const filterText = view.model.filter.text;
          if (!filterText) {
            return true;
          }
          if (!value || value.length === 0) {
            return false;
          }
          //return true if the filter text is contained in the array at any index (ignore case)
          return _.find(value, function (v) {
            return v.toLowerCase().indexOf(filterText.toLowerCase()) !== -1;
          });
        } else {
          return false;
        }
      };
    },
    _sortByHeader: function (view) {
      if (view.model.get("sortable")) {
        if (this.sortedHeaderView !== null) {
          if (this.sortedHeaderView === view) {
            //clicked again on active sort header--> flip order of sort
            view.model.reverse = !view.model.reverse;
          } else {
            //clicked on new header-->remove sorting visual from the previously sorted header
            this.sortedHeaderView.model.sorted = false;
            this.sortedHeaderView.render();
            view.model.reverse = view.model.get("defaultReversed"); //allows field to be reversed by default
          }
        } else {
          view.model.reverse = view.model.get("defaultReversed");
        }
        this.sortedHeaderView = view;
        this.sortedHeaderView.model.sorted = true;
        console.log("sort " + view.model.get("headerName"));
        //make sure we close any filter menus
        this._closeFilterMenu(view);
        this.sortedHeaderView.render();
        Messenger.vent.trigger(this.namespacePrefix + "sort:list", {
          header: view.model,
          reverse: view.model.reverse,
        });
      }
    },
    _setUpVents: function () {
      Messenger.vent.on(
        this.namespacePrefix + "changed:checkboxes",
        function () {
          if (this.collection) {
            //This is the function that actually changes the "master" checkbox
            const checkedBoxes = Messenger.reqres.request(this.namespacePrefix + "checked:models");
            //find filtered collection size
            let collectionLength = 0;
            for (let i = 0; i < this.collection.length; i++) {
              if (!this.collection.at(i).filteredOut) {
                collectionLength++;
              }
            }
            //case1: Master checkbox is checked, and then a checkbox is unchecked-->Uncheck master checkbox
            if (this.headers.checked && checkedBoxes.length < collectionLength) {
              this.headers.checked = false; //we keep track of the master checkbox off of the headers collection
              this.view.render();
            } //case2: Master checkbox is unchecked, and all the checkboxes are checked-->Check master checkbox
            else if (
              !this.headers.checked &&
              collectionLength > 0 &&
              checkedBoxes.length === collectionLength
            ) {
              this.headers.checked = true;
              this.view.render();
            } //case 3:collection length is 0-->uncheck the checkbox
            else if (collectionLength === 0) {
              this.headers.checked = false;
              this.view.render();
            }
          }
        },
        this,
      );

      //set an external filter function to apply to the sortable list.
      //The function must take a model as a parameter and return true to keep the model and false to filter it out
      Messenger.vent.on(
        this.namespacePrefix + "set:external:filter",
        function (externalFilterFunction) {
          this.externalFilterFunction = externalFilterFunction;
          //fire event to filter list with the provided filter function
          Messenger.vent.trigger(this.namespacePrefix + "filter:list", this._filterFunction());
        },
        this,
      );

      Messenger.vent.on(
        this.namespacePrefix + "remove:external:filter",
        function () {
          delete this.externalFilterFunction;
          //fire event to filter list with the provided filter function
          Messenger.vent.trigger(this.namespacePrefix + "filter:list", this._filterFunction());
        },
        this,
      );

      //Handle rerendering the headers when changing the visible headers
      Messenger.vent.on(
        this.namespacePrefix + "headers:changed",
        function () {
          if (this.view) {
            //make sure that we clear the filters from any hidden columns
            this.headers.each(function (header) {
              if (header.get("hidden")) {
                header.filterFunction = null;
                header.filter = null;
              }
            }, this);
            this.view.render();
            //redraw the body by refiltering the list
            Messenger.vent.trigger(this.namespacePrefix + "filter:list", this._filterFunction());
          }
        },
        this,
      );
    },
  });
});
